import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  BooleanInput,
  DateTimeInput,
  Form,
  Loading,
  NumberInput,
  TextInput,
  minValue,
  required,
  useDataProvider,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { NotAuthorized, RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess, type AccessibleRegion } from '../auth/permissions';
import type { AdminDataProvider, Market } from '../dataProvider';
import type { PromoCode } from '../dataProvider/types';
import { formatEffect } from './promoState';

/**
 * Promo code edit. Deactivate-not-delete (an `active` toggle, NO delete button,
 * same reasoning as charities: a code with a used-count history is never
 * destroyed). `market` and `effectType` are fixed at creation and read-only here.
 *
 * THE VALUE-DIRECTION GUARD (D-promo-codes). The trust split is by the direction
 * of value, not the resource:
 *  - The value-REDUCING direction (deactivate, edit the code string, pull the
 *    start date) is promo_codes:write, which editor holds.
 *  - The value-COMMITTING direction (raise effectValue or maxRedemptions, extend
 *    validTo) is promo_codes:issue, super_admin only.
 * Enforced two ways: the value fields are read-only for a non-issuer, AND the
 * submit handler only writes them when the actor holds promo_codes:issue, so
 * reaching past the disabled inputs changes nothing. usedCount is display-only and
 * never an input (the engine writes it back).
 *
 * Audit for free: saving calls dataProvider.update('promoCodes'); the wrapper
 * records the diff. The route is gated promo_codes:write; region-scoped.
 */

interface PromoEditValues {
  code: string;
  effectValue: number;
  validFrom: string;
  validTo: string;
  maxRedemptions: number | string | null;
  active: boolean;
}

const toIso = (v: string): string => new Date(v).toISOString();

const PromoCodeEditForm = ({
  record,
  canIssue,
}: {
  record: PromoCode;
  canIssue: boolean;
}) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const defaultValues: PromoEditValues = {
    code: record.code,
    effectValue: record.effectValue,
    validFrom: record.validFrom,
    validTo: record.validTo,
    maxRedemptions: record.maxRedemptions ?? '',
    active: record.active,
  };

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as PromoEditValues;

    // Build a MINIMAL patch of genuinely-changed fields, so the audit diff is
    // exactly the change (dates compared by instant, not string, to ignore the
    // datetime input's local round-trip).
    const data: Record<string, unknown> = {};
    const previousData: Record<string, unknown> = {};
    const sameInstant = (a: string, b: string): boolean =>
      Date.parse(a) === Date.parse(b);
    const setIf = (
      key: string,
      next: unknown,
      prev: unknown,
      equal: (a: never, b: never) => boolean = (a, b) => a === b,
    ) => {
      if (!equal(next as never, prev as never)) {
        data[key] = next;
        previousData[key] = prev;
      }
    };

    // Always-writable (value-reducing / descriptive) fields.
    setIf('code', values.code.trim().toUpperCase(), record.code);
    setIf('validFrom', toIso(values.validFrom), record.validFrom, sameInstant);
    setIf('active', values.active, record.active);

    // Value-committing fields: written ONLY by an issuer, so a non-issuer reaching
    // past the disabled inputs changes nothing.
    if (canIssue) {
      const max =
        values.maxRedemptions === '' || values.maxRedemptions == null
          ? null
          : Number(values.maxRedemptions);
      setIf('effectValue', Number(values.effectValue), record.effectValue);
      setIf('validTo', toIso(values.validTo), record.validTo, sameInstant);
      setIf('maxRedemptions', max, record.maxRedemptions);
    }

    try {
      if (Object.keys(data).length > 0) {
        await dataProvider.update('promoCodes', {
          id: record.id,
          data,
          previousData: { id: record.id, ...previousData },
        });
      }
      notify('Promo code updated', { type: 'success' });
      redirect('list', 'promoCodes');
    } catch {
      notify('Could not save the promo code', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Edit {record.code}
      </Typography>
      <Form defaultValues={defaultValues} onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextInput
              source="code"
              label="Code"
              validate={required('Code is required')}
              fullWidth
            />
            {/* Market + effect type are fixed at creation, read-only here. */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Market (set at creation)
              </Typography>
              <Typography variant="body1">
                {record.market.toUpperCase()}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Effect (type set at creation)
              </Typography>
              <Typography variant="body1">{formatEffect(record)}</Typography>
            </Box>
            {/* Value-committing fields: read-only unless the actor can issue. */}
            <NumberInput
              source="effectValue"
              label="Effect value"
              disabled={!canIssue}
              validate={[required('Effect value is required'), minValue(0)]}
            />
            <DateTimeInput source="validFrom" label="Valid from" />
            <DateTimeInput
              source="validTo"
              label="Valid to"
              disabled={!canIssue}
            />
            <NumberInput
              source="maxRedemptions"
              label="Max redemptions (blank = uncapped)"
              disabled={!canIssue}
              validate={minValue(0)}
            />
            {/* usedCount is DISPLAY-ONLY: the engine writes it back, never this
                surface, so it is text and never an input. */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Used count (engine-maintained)
              </Typography>
              <Typography variant="body1">{record.usedCount}</Typography>
            </Box>
            {/* The soft-disable control — deactivate/reactivate (no delete). */}
            <BooleanInput source="active" label="Active" />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Save
          </Button>
          <Button type="button" onClick={() => redirect('list', 'promoCodes')}>
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** The edit view. Exported for testing without the access wrapper. */
export const PromoCodeEditView = () => {
  const { id } = useParams();
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  const { data: record, isLoading } = useGetOne<PromoCode>('promoCodes', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;

  // Region guard — cannot edit a code outside the admin's market.
  const inScope = accessibleRegions.includes(
    (record.market as Market).toUpperCase() as AccessibleRegion,
  );
  if (!inScope) return <NotAuthorized />;

  const canIssue = hasAccess(
    { role, accessibleRegions },
    { capability: 'promo_codes:issue' },
  );

  return <PromoCodeEditForm record={record} canIssue={canIssue} />;
};

/** Gated entry point registered as the `promoCodes` Resource's edit. */
export const PromoCodeEdit = () => (
  <RequireAccess capability="promo_codes:write">
    <PromoCodeEditView />
  </RequireAccess>
);
